import { DeepReactive, unwrapReactive } from '@jack-henry/frontend-utils/lit';
import { CompanyAccount } from '@treasury/domain/company/company-account.entity';
import { TransactionCode } from '@treasury/domain/reports/reports.service';
import { TmBaseComponent } from '@treasury/presentation';
import { TmForm, type TmValueChanged } from '@treasury/presentation/components/forms';
import {
    type CurrencyRange,
    CurrencyRangeType,
} from '@treasury/presentation/components/tm-currency-range';
import { type DateRange, DateRangeType } from '@treasury/presentation/components/tm-date-range';
import type { FormLayoutResponsiveStep } from '@vaadin/form-layout';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { mapAccountToVm, mapPaymentTypeToVm, ReportFiltersVm } from '../data/report-filters.vm';

import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-multi-select';
import '@treasury/presentation/components/tm-radio-group';
import '@treasury/presentation/components/tm-select';
import '@treasury/presentation/components/tm-text-field';
// eslint-disable-next-line no-duplicate-imports
import '@vaadin/form-layout';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-date-range';
// eslint-disable-next-line no-duplicate-imports
import '@treasury/presentation/components/tm-currency-range';

type FormModel = Pick<
    ReportFiltersVm,
    | 'accountTypes'
    | 'accounts'
    | 'sortBy'
    | 'isAscendingSort'
    | 'checkNumberOperator'
    | 'fromPaymentId'
    | 'toPaymentId'
    | 'paymentTypes'
    | 'transactionDirection'
    | 'transactionType'
> & { dateRange: DateRange; currencyRange: CurrencyRange };

const tagName = 'transaction-report-filters';
@customElement(tagName)
export class TransactionReportFilters extends TmBaseComponent {
    @property({ type: Boolean, reflect: true })
    public range = false;

    @DeepReactive()
    public filters?: ReportFiltersVm;

    @state()
    private showCheckNumberRange = false;

    private readonly form = createRef<TmForm<FormModel>>();

    private readonly responsiveSteps: FormLayoutResponsiveStep[] = [
        { minWidth: 0, columns: 1 },
        { minWidth: '768px', columns: 2 },
        { minWidth: '1024px', columns: 3 },
    ];

    public render() {
        if (!this.filters) {
            return nothing;
        }
        return html`
            <tm-form
                ${ref(this.form)}
                .validator=${() => this.filters?.isValid}
                @form-changed=${() => {
                    this.dispatchFiltersChanged();
                }}
            >
                <vaadin-form-layout .responsiveSteps=${this.responsiveSteps}>
                    ${this.range
                        ? html`<tm-date-range
                              name="dateRange"
                              label="Date"
                              required
                              .initial=${this.filters.initialDateRange}
                              .maxDate=${new Date()}
                              @value-changed=${(e: TmValueChanged<DateRange>) => {
                                  const { value } = e.detail;
                                  if (value) {
                                      if (this.filters) {
                                          this.filters.dateType = value.rangeType;
                                          this.filters.fromDate = value.startDate
                                              ? format(value.startDate, 'P')
                                              : '';
                                          this.filters.toDate = value.endDate
                                              ? format(value.endDate, 'P')
                                              : '';
                                          this.filters.specificDate =
                                              value.startDate &&
                                              value.rangeType === DateRangeType.SpecificDate
                                                  ? format(value.startDate, 'P')
                                                  : '';
                                      }
                                  }
                              }}
                          ></tm-date-range>`
                        : nothing}
                    <tm-select
                        name="accountTypes"
                        label="Account Type"
                        .initial=${this.filters.accountTypesString}
                        .items=${this.filters.accountTypeItemsString}
                        @value-changed=${(e: CustomEvent) => {
                            if (e.detail.value) {
                                if (this.filters) {
                                    this.filters.accountTypes = e.detail.value;
                                }
                                this.requestUpdate();
                            }
                        }}
                    ></tm-select>
                    <tm-multi-select
                        name="accounts"
                        label="Accounts"
                        .allowSelectAll=${true}
                        .allSelectedText=${'All Accounts'}
                        .items=${this.filters.filteredAccounts}
                        .initial=${[...this.filters.selectedAccounts]}
                        .mapItemToVm=${mapAccountToVm}
                        @value-changed=${(e: TmValueChanged<CompanyAccount[]>) => {
                            if (!e.detail.value) {
                                return;
                            }
                            if (this.filters) {
                                this.filters.accounts = e.detail.value;
                            }
                        }}
                    ></tm-multi-select>
                    <div>
                        <tm-select
                            name="sortBy"
                            label="Sort By"
                            .initial=${this.filters.sortByInitial}
                            .items=${this.filters.transactionSortByItems}
                            @value-changed=${(e: CustomEvent) => {
                                if (e.detail.value && this.filters) {
                                    this.filters.sortBy = e.detail.value;
                                }
                            }}
                        ></tm-select>
                        <tm-radio-group
                            name="isAscendingSort"
                            .initial=${this.filters.displayIsAscendingSort}
                            .options=${this.filters.isAscendingSortOptions}
                            @value-changed=${(e: CustomEvent) => {
                                if (e.detail.value && this.filters) {
                                    this.filters.displayIsAscendingSort = e.detail.value;
                                }
                            }}
                        ></tm-radio-group>
                    </div>
                    <div>
                        <tm-select
                            name="checkNumberOperator"
                            label="Check Number"
                            .initial=${this.filters.initialCheckNumberOperator}
                            .items=${[
                                {
                                    label: 'Specific Check Number',
                                    value: 'Specific Check Number',
                                },
                                {
                                    label: 'Check Number Range',
                                    value: 'Check Number Range',
                                },
                            ]}
                            @value-changed=${(e: CustomEvent) => {
                                if (!e.detail.value) {
                                    return;
                                }
                                if (this.filters) {
                                    this.filters.checkNumberOperator = e.detail.value as string;
                                    this.showCheckNumberRange =
                                        e.detail.value === 'Check Number Range';
                                }
                            }}
                        ></tm-select>
                        ${this.renderCheckNumberRange()}
                    </div>
                    <tm-currency-range
                        name="currencyRange"
                        label="Amount"
                        .currencyRangeOptions=${[
                            CurrencyRangeType.AllAmounts,
                            CurrencyRangeType.SpecificAmount,
                            CurrencyRangeType.Between,
                            CurrencyRangeType.LessThan,
                            CurrencyRangeType.GreaterThan,
                        ]}
                        @value-changed=${(e: TmValueChanged<CurrencyRange>) => {
                            const { value } = e.detail;
                            if (!value || !this.filters) {
                                return;
                            }
                            const { rangeType, floorAmount, ceilAmount, specificAmount } = value;
                            this.filters.amountOperator =
                                rangeType === 'All Amounts' ? undefined : rangeType;
                            this.filters.fromAmount = floorAmount ?? specificAmount ?? undefined;
                            this.filters.toAmount = ceilAmount ?? undefined;
                        }}
                    ></tm-currency-range>
                    <tm-multi-select
                        name="paymentTypes"
                        label="Transaction Type"
                        .allowSelectAll=${true}
                        .items=${this.filters.paymentTypes}
                        .mapItem=${mapPaymentTypeToVm}
                        @value-changed=${(e: TmValueChanged<TransactionCode[]>) => {
                            if (!e.detail.value) {
                                return;
                            }

                            if (this.filters) {
                                this.filters.paymentTypes = e.detail.value;
                            }
                        }}
                    ></tm-multi-select>
                    <div>
                        <tm-select
                            name="transactionDirection"
                            label="Transaction Direction"
                            .initial=${'Both'}
                            .items=${this.filters.transactionDirectionItems}
                            @value-changed=${(e: CustomEvent) => {
                                if (e.detail.value && this.filters) {
                                    this.filters.transactionDirection = e.detail.value as string;
                                }
                            }}
                        ></tm-select>
                        <tm-radio-group
                            name="transactionType"
                            .initial=${this.filters.initialTransactionDirection}
                            .options=${this.filters.transactionTypeItems}
                            @value-changed=${(e: CustomEvent) => {
                                if (e.detail.value && this.filters) {
                                    this.filters.transactionType = e.detail.value as string;
                                }
                            }}
                        ></tm-radio-group>
                    </div>
                </vaadin-form-layout>
            </tm-form>
        `;
    }

    private renderCheckNumberRange() {
        if (this.showCheckNumberRange) {
            return html`<tm-text-field
                    name="fromPaymentId"
                    .initial=${this.filters?.fromPaymentId}
                    @value-changed=${(e: CustomEvent) => {
                        if (this.filters) {
                            this.filters.fromPaymentId = parseInt(e.detail.value as string);
                        }
                    }}
                ></tm-text-field>
                to
                <tm-text-field
                    name="toPaymentId"
                    .initial=${this.filters?.toPaymentId}
                    @value-changed=${(e: CustomEvent) => {
                        if (this.filters) {
                            this.filters.toPaymentId = parseInt(e.detail.value as string);
                        }
                    }}
                ></tm-text-field>`;
        } else {
            return html`<tm-text-field
                name="fromPaymentId"
                .initial=${this.filters?.initialCheckNumber}
                .placeholder=${'All Checks'}
                @value-changed=${(e: CustomEvent) => {
                    if (this.filters) {
                        this.filters.fromPaymentId = parseInt(e.detail.value as string);
                    }
                }}
            ></tm-text-field>`;
        }
    }

    private dispatchFiltersChanged() {
        this.dispatchEvent(
            new CustomEvent('filters-changed', {
                detail: {
                    filters: unwrapReactive(this.filters),
                    isValid: this.form.value?.isValid ?? false,
                },
                bubbles: true,
                composed: true,
            })
        );
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TransactionReportFilters;
    }
}
